import Head from 'next/head'
import { useEffect, useMemo, useRef, useState } from 'react'
import AnalyticsSidebar from 'src/components/overview/AnalyticsSidebar'
import ConversionsSection from 'src/components/overview/sections/ConversionsSection'
import DiscountsSection from 'src/components/overview/sections/DiscountsSection'
import OrdersSection from 'src/components/overview/sections/OrdersSection'
import OverviewSection from 'src/components/overview/sections/OverviewSection'
import PaymentsSection from 'src/components/overview/sections/PaymentsSection'
import { MagnifyingGlass, Acorn, Airplane } from '@phosphor-icons/react'

import AnalyticsProvider, { useAnalytics } from 'src/lib/contexts/AnalyticsContext'

import { CheckSection } from 'src/components/overview/charts/common/CheckWidget'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Filters } from 'src/components/overview/Filters'
import { WidgetSections } from 'src/lib/contexts/AnalyticsContext/types'
import { Button, Input } from '@shopflo/ui'
import UserLayoutV2 from 'src/layouts/UserLayoutV2'
import { AlertsProvider, useAlertsContext } from 'src/lib/contexts/AlertsProvider'
import AlertConfigurationDialog from 'src/components/alerts/AlertConfigurationDialog'
import { getMetricMatchKey } from 'src/lib/utils/alertsHelpers'

const queryClient = new QueryClient()

const baseSections = {
  primary: {
    sections: [
      { id: WidgetSections.conversion, label: 'Conversions', icon: 'fire' },
      { id: WidgetSections.payments, label: 'Payments', icon: 'creditCard' },
      { id: WidgetSections.orders, label: 'Orders', icon: 'package' },
      { id: WidgetSections.discounts, label: 'Discounts', icon: 'tag' }
    ],
    title: ''
  },
  secondary: {
    sections: [
      { id: WidgetSections.upsells, label: 'Upsells', icon: 'package' },
      { id: WidgetSections.rewards, label: 'Rewards', icon: 'package' }
    ],
    title: 'SHOPFLO APPS'
  }
}

const ALERT_METRIC_CANDIDATES_BY_WIDGET_ID: Record<string, string[]> = {
  conversion_rate: ['conversion_rate'],
  payment_prepaid_share: ['prepaid_share'],
  orders: ['shopflo_orders_count', 'shopflo_orders'],
  orders_sales: ['shopflo_orders_sales', 'shopflo_sales', 'sales']
}

const AnalyticsComponents = () => {
  const {
    getters: { widgetConfig },
    actions: { setAlertDialogState },
    alertDialog
  } = useAnalytics()

  
  const alerts = useAlertsContext(state => state.alerts)
  const widgetId = alertDialog.widgetId ?? ''
  const metricCandidates = ALERT_METRIC_CANDIDATES_BY_WIDGET_ID[widgetId] || [widgetId]
  const initialMetric =
    metricCandidates.find(candidate =>
      alerts.some(alert => getMetricMatchKey(alert.alertDetails.metricName) === getMetricMatchKey(candidate))
    ) || metricCandidates[0]
  const selectedAlert = initialMetric
    ? alerts.find(
        alert => getMetricMatchKey(alert.alertDetails.metricName) === getMetricMatchKey(initialMetric)
      ) ?? null
    : null
  const [activeSection, setActiveSection] = useState<WidgetSections>()
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const observerRef = useRef<IntersectionObserver | null>(null)

  const filteredSections = useMemo(
    () => baseSections.primary.sections.filter(section => widgetConfig[section.id as keyof typeof widgetConfig]),
    [widgetConfig]
  )
  const filteredSecondarySections = useMemo(
    () => baseSections.secondary.sections.filter(section => widgetConfig[section.id as keyof typeof widgetConfig]),
    [widgetConfig]
  )

  const sections = useMemo(() => {
    return {
      primary: {
        sections: filteredSections,
        title: ''
      },
      secondary: {
        sections: filteredSecondarySections,
        title: 'SHOPFLO APPS'
      }
    }
  }, [filteredSections, filteredSecondarySections])

  useEffect(() => {
    if (filteredSections.length > 0 && !activeSection) {
      setActiveSection(filteredSections[0].id as WidgetSections)
    }
  }, [filteredSections, activeSection])

  useEffect(() => {
    const mainContent = document.querySelector<HTMLElement>('#main-content')
    if (mainContent && filteredSections.length > 0) {
      const handleScroll = () => {
        if (mainContent.scrollTop <= 50) {
          setActiveSection(filteredSections[0].id as WidgetSections)
        }
      }
      mainContent.addEventListener('scroll', handleScroll, { passive: true })
      return () => mainContent.removeEventListener('scroll', handleScroll)
    }
  }, [filteredSections])

  useEffect(() => {
    const mainContent = document.querySelector<HTMLElement>('#main-content')
    if (!mainContent) return

    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id as WidgetSections)
          }
        })
      },
      {
        root: mainContent,
        threshold: 0.1
      }
    )

    Object.values(sectionRefs.current).forEach(el => {
      if (el) observerRef.current?.observe(el)
    })

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [sections])

  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el
    if (el && observerRef.current) {
      observerRef.current.observe(el)
    }
  }

  const handleSectionClick = (sectionId: WidgetSections) => {
    setActiveSection(sectionId)
    const element = sectionRefs.current[sectionId]
    const mainContent = document.querySelector<HTMLElement>('#main-content')

    if (element && mainContent) {
      const elementRect = element.getBoundingClientRect()
      const mainContentRect = mainContent.getBoundingClientRect()
      const elementTopRelativeToMain = elementRect.top - mainContentRect.top + mainContent.scrollTop

      mainContent.scrollTo({
        top: elementTopRelativeToMain - 40,
        behavior: 'smooth'
      })
    }
  }

  return (
    <>
      <div className='p-8'>
        <Filters />

        <section id='overview' ref={setSectionRef('overview')} className='mt-3'>
          <OverviewSection />
        </section>

        <hr className='border-t border-gray-light mb-4' />

        <div className='flex flex-col w-full relative'>
          <div
            id='analytics-sidebar'
            className='w-full h-fit rog-white p-4 transition-all duration-200 ease-in-out rounded-xl shadow-[0px_2px_6px_0px_#0000000D] sticky border-1 border-gradient-to-bl from-[#93E5D6] to-[#F0F0F0] bg-white z-10 top-5'
            data-track-id='analytics-sidebar'
          >
            <AnalyticsSidebar sections={sections} activeSection={activeSection} setActiveSection={handleSectionClick} />
          </div>

          {/* Main Content */}
          <div className='flex-1 py-6 z-1'>
            <div className='mx-auto'>
              {/* All sections */}
              <div className='space-y-12'>
                <CheckSection widgetSection={WidgetSections.conversion}>
                  <section
                    id={WidgetSections.conversion}
                    ref={setSectionRef(WidgetSections.conversion)}
                    className='min-h-[500px]'
                  >
                    <ConversionsSection />
                  </section>
                </CheckSection>
                <CheckSection widgetSection={WidgetSections.payments}>
                  <section
                    id={WidgetSections.payments}
                    ref={setSectionRef(WidgetSections.payments)}
                    className='min-h-[500px]'
                  >
                    <PaymentsSection />
                  </section>
                </CheckSection>
                <CheckSection widgetSection={WidgetSections.orders}>
                  <section
                    id={WidgetSections.orders}
                    ref={setSectionRef(WidgetSections.orders)}
                    className='min-h-[500px]'
                  >
                    <OrdersSection />
                  </section>
                </CheckSection>
                <CheckSection widgetSection={WidgetSections.discounts}>
                  <section
                    id={WidgetSections.discounts}
                    ref={setSectionRef(WidgetSections.discounts)}
                    className='min-h-[500px]'
                  >
                    <DiscountsSection />
                  </section>
                </CheckSection>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertConfigurationDialog
        isOpen={alertDialog.isOpen}
        setIsOpen={open => {
          if (!open) setAlertDialogState(false)
        }}
        selectedAlert={selectedAlert}
        initialMetric={initialMetric}
        initialThreshold={alertDialog.initialThreshold}
      />
    </>
  )
}

const AnalyticsPage = () => {
  return (
    <>
      <Head>
        <title>Store Analytics | Shopflo</title>
      </Head>
      <AlertsProvider>
        <AnalyticsProvider>
          <QueryClientProvider client={queryClient}>
            <AnalyticsComponents />
          </QueryClientProvider>
        </AnalyticsProvider>
      </AlertsProvider>
    </>
  )
}

AnalyticsPage.getLayout = (page: React.ReactElement) => <UserLayoutV2>{page}</UserLayoutV2>

export default AnalyticsPage
